import React, { useState, useEffect } from 'react';

const InsuranceInfoModal = ({ isOpen, onClose, onSave, initialData = null }) => {
  const [formData, setFormData] = useState({
    insurance_company: '',
    policy_holder_name: '',
    insurance_number: '',
    issuance_date: '',
    expiration_date: '',
    group_number: ''
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Popular insurance companies in Kuwait
  const kuwaitInsuranceCompanies = [
    'Kuwait Insurance Company (KIC)',
    'Warba Insurance Company',
    'Al-Ahleia Insurance Company',
    'Gulf Insurance Group (GIG)',
    'Boubyan Insurance Company',
    'United Insurance Company',
    'Al Safat Insurance Company',
    'MEDGULF',
    'Other'
  ];

  useEffect(() => {
    if (initialData) {
      setFormData({
        insurance_company: initialData.insurance_company || '',
        policy_holder_name: initialData.policy_holder_name || '',
        insurance_number: initialData.insurance_number || '',
        issuance_date: initialData.issuance_date || '',
        expiration_date: initialData.expiration_date || '',
        group_number: initialData.group_number || ''
      });
    } else {
      // Reset form for new entry
      setFormData({
        insurance_company: '',
        policy_holder_name: '',
        insurance_number: '',
        issuance_date: '',
        expiration_date: '',
        group_number: ''
      });
    }
    setErrors({});
  }, [initialData, isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.insurance_company) {
      newErrors.insurance_company = 'Insurance company is required';
    }
    
    if (!formData.policy_holder_name) {
      newErrors.policy_holder_name = 'Policy holder name is required';
    }
    
    if (!formData.insurance_number) {
      newErrors.insurance_number = 'Insurance number is required';
    }
    
    if (!formData.issuance_date) {
      newErrors.issuance_date = 'Issuance date is required';
    }
    
    if (!formData.expiration_date) {
      newErrors.expiration_date = 'Expiration date is required';
    }
    
    // Validate that expiration date is after issuance date
    if (formData.issuance_date && formData.expiration_date) {
      if (new Date(formData.expiration_date) <= new Date(formData.issuance_date)) {
        newErrors.expiration_date = 'Expiration date must be after issuance date';
      }
    }
    
    // Validate that expiration date is not in the past
    if (formData.expiration_date) {
      if (new Date(formData.expiration_date) < new Date()) {
        newErrors.expiration_date = 'Expiration date cannot be in the past';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      console.error('Error saving insurance information:', error);
      setErrors({ submit: 'Failed to save insurance information. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">
              {initialData ? 'Edit Insurance Information' : 'Add Insurance Information'}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-2"
              disabled={isLoading}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Form Content */}
          <div className="p-6 space-y-6">
            {errors.submit && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="text-red-800 text-sm">{errors.submit}</div>
              </div>
            )}

            {/* Insurance Company */}
            <div>
              <label htmlFor="insurance_company" className="block text-sm font-medium text-gray-700 mb-2">
                Insurance Company *
              </label>
              <select
                id="insurance_company"
                name="insurance_company"
                value={formData.insurance_company}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.insurance_company ? 'border-red-300' : 'border-gray-300'
                }`}
                disabled={isLoading}
              >
                <option value="">Select Insurance Company</option>
                {kuwaitInsuranceCompanies.map((company) => (
                  <option key={company} value={company}>
                    {company}
                  </option>
                ))}
              </select>
              {errors.insurance_company && (
                <p className="mt-1 text-sm text-red-600">{errors.insurance_company}</p>
              )}
            </div>

            {/* Policy Holder Name */}
            <div>
              <label htmlFor="policy_holder_name" className="block text-sm font-medium text-gray-700 mb-2">
                Policy Holder Name *
              </label>
              <input
                type="text"
                id="policy_holder_name"
                name="policy_holder_name"
                value={formData.policy_holder_name}
                onChange={handleInputChange}
                placeholder="Enter full name as shown on insurance card"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.policy_holder_name ? 'border-red-300' : 'border-gray-300'
                }`}
                disabled={isLoading}
              />
              {errors.policy_holder_name && (
                <p className="mt-1 text-sm text-red-600">{errors.policy_holder_name}</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Insurance Number */}
              <div>
                <label htmlFor="insurance_number" className="block text-sm font-medium text-gray-700 mb-2">
                  Insurance Number *
                </label>
                <input
                  type="text"
                  id="insurance_number"
                  name="insurance_number"
                  value={formData.insurance_number}
                  onChange={handleInputChange}
                  placeholder="e.g., KIC-123456789"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono ${
                    errors.insurance_number ? 'border-red-300' : 'border-gray-300'
                  }`}
                  disabled={isLoading}
                />
                {errors.insurance_number && (
                  <p className="mt-1 text-sm text-red-600">{errors.insurance_number}</p>
                )}
              </div>

              {/* Group Number */}
              <div>
                <label htmlFor="group_number" className="block text-sm font-medium text-gray-700 mb-2">
                  Group Number
                </label>
                <input
                  type="text"
                  id="group_number"
                  name="group_number"
                  value={formData.group_number}
                  onChange={handleInputChange}
                  placeholder="e.g., GRP-001"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Issuance Date */}
              <div>
                <label htmlFor="issuance_date" className="block text-sm font-medium text-gray-700 mb-2">
                  Issuance Date *
                </label>
                <input
                  type="date"
                  id="issuance_date"
                  name="issuance_date"
                  value={formData.issuance_date}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.issuance_date ? 'border-red-300' : 'border-gray-300'
                  }`}
                  disabled={isLoading}
                />
                {errors.issuance_date && (
                  <p className="mt-1 text-sm text-red-600">{errors.issuance_date}</p>
                )}
              </div>

              {/* Expiration Date */}
              <div>
                <label htmlFor="expiration_date" className="block text-sm font-medium text-gray-700 mb-2">
                  Expiration Date *
                </label>
                <input
                  type="date"
                  id="expiration_date"
                  name="expiration_date"
                  value={formData.expiration_date}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.expiration_date ? 'border-red-300' : 'border-gray-300'
                  }`}
                  disabled={isLoading}
                />
                {errors.expiration_date && (
                  <p className="mt-1 text-sm text-red-600">{errors.expiration_date}</p>
                )}
              </div>
            </div>

            {/* Information Note */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex">
                <svg className="w-5 h-5 text-blue-400 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Privacy & Security</p>
                  <p>Your insurance information is encrypted and only visible to you. Healthcare providers will only see necessary details when you book appointments.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              disabled={isLoading}
            >
              {isLoading && (
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {isLoading ? 'Saving...' : (initialData ? 'Update Insurance' : 'Save Insurance')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InsuranceInfoModal;